import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChefHat, XCircle, HelpCircle, ChevronDown } from 'lucide-react';
import buttonStyles from '../styles/components/Button.module.css';

const SAMPLE_MAILTO =
  'mailto:contact@folsommicrogreens.com?subject=Restaurant%20sample%20request&body=Restaurant%20name%3A%20%0AContact%20name%3A%20%0APhone%3A%20%0ACuisine%2Fmenu%20style%3A%20%0AVarieties%20you%27d%20like%20to%20sample%3A%20%0AEstimated%20weekly%20volume%3A%20%0APreferred%20format%20(clamshells%20or%20bulk)%3A%20';

const PAIN_POINTS = [
  'Show up half-spoiled on arrival',
  'Last only a few days in the cooler',
  'Force you to reorder two or three times a week',
  "Make you wonder if Saturday's greens will hold for Sunday service",
  'Drive food cost up because half the case ends up in the trash',
  'Disappear from the distributor list right when your menu needs them',
];

const FAQS = [
  {
    q: 'Can I see your menu and pricing?',
    a: "Yes — we share full pricing during a quick 15-minute meeting (in person or by phone) so we can match it to what your kitchen actually uses. Reach out and we'll send the menu over with a couple of free sample clamshells.",
  },
  {
    q: 'How long do your microgreens last?',
    a: "Ten to fourteen days in the cooler. We cut the morning we deliver, so you're getting them at the start of their shelf life — not at the end like most distributor product.",
  },
  {
    q: 'How fast do you deliver after harvest?',
    a: 'Same day for restaurant accounts in and around Folsom — harvested Thursday morning, on your line by 1 PM Thursday.',
  },
  {
    q: 'Why do you only do weekly recurring orders?',
    a: 'Microgreens take 8–12 days from seed to harvest, so we sow specifically to your order. Standing orders let us plan production, keep waste low, and keep your price competitive. You can skip, pause, or change the order anytime — just give us a heads up.',
  },
  {
    q: 'Can I order multiple times per week?',
    a: 'Almost never necessary. Because our greens last 10–14 days and we dial in the right weekly volume for your kitchen, one delivery per week is enough — even for high-volume accounts.',
  },
  {
    q: 'Do you have order minimums or delivery fees?',
    a: 'No minimums and no delivery fees inside our standard delivery zone in and around Folsom. We want this to make your job easier, not harder.',
  },
  {
    q: 'How do payments work?',
    a: 'Check or Zelle on delivery — we keep it simple. No invoicing.',
  },
  {
    q: 'Am I locked into a contract?',
    a: 'No. Your weekly order is "penciled in" so we know what to grow — but you can skip a week, scale up, scale down, or cancel anytime. We just ask for a heads up since we grow lean.',
  },
];

const CUISINES = [
  {
    title: 'Sushi & Poke Bars',
    body: 'Micros that hold their shape under sauce, rice, and raw fish. Top nigiri, finish dragon rolls, brighten poke bowls and raw salmon tacos.',
    recommended: 'wasabi, colorful trio, peppery power',
  },
  {
    title: 'Seasonal American',
    body: 'Plate-ready greens for caprese, halibut, red snapper, brick chicken. The pile of micros that makes an entrée look composed.',
    recommended: 'arugula fusion, pea shoots, mild blend',
  },
  {
    title: 'Cocktail & Brunch Programs',
    body: 'Sunflower in a Bloody Mary, peppery power on avocado toast, micro herbs on a citrus shrub. Garnishes that read across the bar.',
    recommended: 'cilantro, peppery power, colorful trio',
  },
];

const Chefs = () => {
  return (
    <div className="container">
      <Helmet>
        <title>For Chefs & Restaurants | Folsom Microgreens</title>
        <meta
          name="description"
          content="Microgreens for chefs in and around Folsom. 10–14 day shelf life, harvested the morning of delivery, standing weekly orders, no minimums."
        />
        <link rel="canonical" href="https://www.folsommicrogreens.com/chefs" />
      </Helmet>

      {/* Page hero */}
      <section style={{
        textAlign: 'center',
        padding: 'clamp(2rem, 6vw, 3rem) var(--spacing-4) var(--spacing-8)',
      }}>
        <ChefHat style={{ width: '2.5rem', height: '2.5rem', color: 'var(--color-primary)' }} />
        <h1 style={{
          fontSize: 'clamp(1.75rem, 5.5vw, 2.25rem)',
          fontWeight: '700',
          margin: 'var(--spacing-3) 0',
          color: 'var(--color-text)',
          lineHeight: 1.2,
        }}>
          Microgreens for chefs — without the waste.
        </h1>
        <p style={{
          fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
          color: 'var(--color-text-light)',
          maxWidth: '640px',
          margin: '0 auto var(--spacing-6)',
          lineHeight: 1.5,
        }}>
          Harvested the morning of delivery. 10–14 day shelf life. Standing weekly orders dialed in to your kitchen, in clamshells or bulk by the ounce.
          Serving restaurants in and around Folsom.
        </p>
        <div style={{ display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href={SAMPLE_MAILTO} className={buttonStyles.primary} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            Request free samples <ArrowRight style={{ width: '1.2rem', height: '1.2rem' }} />
          </a>
          <a href="tel:+12798427951" className={buttonStyles.outline} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            Or text 279-842-7951
          </a>
        </div>
      </section>

      {/* Pain points */}
      <section style={{
        background: 'var(--color-background-light)',
        padding: 'var(--spacing-10) var(--spacing-6)',
        borderRadius: 'var(--border-radius-lg)',
        margin: 'var(--spacing-8) 0',
      }}>
        <div style={{ maxWidth: '780px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'var(--font-size-3xl)',
            fontWeight: '600',
            marginBottom: 'var(--spacing-2)',
            color: 'var(--color-text)',
          }}>
            Tired of microgreens that…
          </h2>
          <p style={{ textAlign: 'center', color: 'var(--color-text-light)', marginBottom: 'var(--spacing-6)' }}>
            If any of these sound familiar, you're not alone — and you don't have to keep paying for it.
          </p>

          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'var(--spacing-3)',
          }}>
            {PAIN_POINTS.map((point) => (
              <li key={point} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--spacing-2)',
                background: 'var(--color-white)',
                padding: 'var(--spacing-3) var(--spacing-4)',
                borderRadius: 'var(--border-radius)',
                boxShadow: 'var(--shadow)',
              }}>
                <XCircle style={{
                  width: '1.2rem',
                  height: '1.2rem',
                  color: '#dc2626',
                  flexShrink: 0,
                  marginTop: '0.15rem',
                }} />
                <span style={{ color: 'var(--color-text)' }}>{point}</span>
              </li>
            ))}
          </ul>

          <p style={{
            textAlign: 'center',
            marginTop: 'var(--spacing-6)',
            fontSize: 'var(--font-size-lg)',
            color: 'var(--color-text)',
          }}>
            We harvest the morning we deliver. Our microgreens last <strong>10 to 14 days</strong> in the cooler — because you're getting them at the start of their shelf life, not the end.
          </p>
        </div>
      </section>

      {/* On the Plate */}
      <section style={{ padding: 'var(--spacing-10) 0' }}>
        <h2 style={{
          textAlign: 'center',
          fontSize: 'var(--font-size-3xl)',
          fontWeight: '600',
          marginBottom: 'var(--spacing-3)',
          color: 'var(--color-text)',
        }}>
          On the Plate
        </h2>
        <p style={{
          textAlign: 'center',
          fontSize: 'var(--font-size-lg)',
          color: 'var(--color-text-light)',
          maxWidth: '720px',
          margin: '0 auto var(--spacing-8)',
        }}>
          Below are the styles of kitchens we serve and the micros chefs reach for most.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 'var(--spacing-6)',
        }}>
          {CUISINES.map((c) => (
            <div key={c.title} style={{
              background: 'var(--color-white)',
              padding: 'var(--spacing-6)',
              borderRadius: 'var(--border-radius-lg)',
              boxShadow: 'var(--shadow)',
            }}>
              <h3 style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: '600',
                marginBottom: 'var(--spacing-2)',
                color: 'var(--color-text)',
              }}>
                {c.title}
              </h3>
              <p style={{ color: 'var(--color-text-light)', marginBottom: 'var(--spacing-3)' }}>
                {c.body}
              </p>
              <p style={{ color: 'var(--color-text)', fontSize: 'var(--font-size-sm)' }}>
                <strong>Recommended:</strong> {c.recommended}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section style={{
        marginTop: 'var(--spacing-8)',
        background: 'var(--color-background-light)',
        padding: 'var(--spacing-8) var(--spacing-6)',
        borderRadius: 'var(--border-radius-lg)',
        textAlign: 'center',
      }}>
        <h2 style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: '600',
          marginBottom: 'var(--spacing-3)',
          color: 'var(--color-text)',
        }}>
          How it works
        </h2>
        <p style={{ color: 'var(--color-text-light)', marginBottom: 'var(--spacing-4)', maxWidth: '640px', margin: '0 auto var(--spacing-4)' }}>
          Order at least <strong>10 days in advance</strong> · harvested <strong>Thursday morning</strong> · on your line by <strong>1 PM Thursday</strong>.
          <em style={{ display: 'block', marginTop: 'var(--spacing-2)', color: 'var(--color-text-light)' }}>
            Microgreens take 8–12 days from seed to harvest, so we sow to your order — your greens are cut for you, not pulled from inventory.
          </em>
          Clamshells or bulk cut greens by the ounce, packed to your spec.
          <strong> A free clamshell or two on request</strong> for new restaurant accounts in and around Folsom.
        </p>
        <a href={SAMPLE_MAILTO} className={buttonStyles.primary} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          Request free samples <ChefHat style={{ width: '1.2rem', height: '1.2rem' }} />
        </a>
        <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-light)' }}>
          Or text us at <a href="tel:+12798427951" style={{ color: 'var(--color-primary)', fontWeight: '600' }}>279-842-7951</a>
        </p>
      </section>

      {/* FAQ */}
      <section id="faq" style={{
        padding: 'var(--spacing-12) 0',
        margin: 'var(--spacing-8) 0',
        scrollMarginTop: '5rem',
      }}>
        <div className="container" style={{ maxWidth: '820px' }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-6)' }}>
            <HelpCircle style={{ width: '2rem', height: '2rem', color: 'var(--color-primary)' }} />
            <h2 style={{
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '600',
              margin: 'var(--spacing-2) 0',
              color: 'var(--color-text)',
            }}>
              Frequently Asked Questions
            </h2>
            <p style={{ color: 'var(--color-text-light)' }}>
              Quick answers to what chefs ask us most.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
            {FAQS.map((item, idx) => (
              <details key={idx} style={{
                background: 'var(--color-white)',
                borderRadius: 'var(--border-radius-lg)',
                boxShadow: 'var(--shadow)',
                overflow: 'hidden',
              }}>
                <summary style={{
                  cursor: 'pointer',
                  padding: 'var(--spacing-4) var(--spacing-5)',
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: '600',
                  color: 'var(--color-text)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--spacing-3)',
                  listStyle: 'none',
                }}>
                  <span>{item.q}</span>
                  <ChevronDown style={{
                    width: '1.2rem',
                    height: '1.2rem',
                    color: 'var(--color-primary)',
                    flexShrink: 0,
                  }} />
                </summary>
                <div style={{
                  padding: '0 var(--spacing-5) var(--spacing-4)',
                  color: 'var(--color-text-light)',
                  lineHeight: '1.6',
                }}>
                  {item.a}
                </div>
              </details>
            ))}
          </div>

          <p style={{ textAlign: 'center', marginTop: 'var(--spacing-6)', color: 'var(--color-text-light)' }}>
            Still have a question? <a href={SAMPLE_MAILTO} style={{ color: 'var(--color-primary)', fontWeight: '600' }}>Request free samples</a> or text us at <a href="tel:+12798427951" style={{ color: 'var(--color-primary)', fontWeight: '600' }}>279-842-7951</a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Chefs;
